import { FC, ReactElement, ReactNode, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import classnames from 'classnames'

import { Button, Icon, IconName } from '@/components'
import { trans } from '@/helpers/trans'

import { version } from '../../../package.json'

import './CollapsibleNavigation.scss'

// Labels can contain markup, e.g. the language item shows a flag and items can show an unread badge
interface NavLinkItem {
    label: ReactNode
    link: string
}

export interface NavItem extends NavLinkItem {
    icon?: IconName
    children: NavLinkItem[]
}

interface CollapsibleNavigationProps {
    isActive: boolean
    navItems: Record<string, NavItem>
    navItemsHeaders: string[]
    onLogoutClick: () => void
    onNavLinkClick: (header: string) => void
    className?: string
}

export const CollapsibleNavigation: FC<CollapsibleNavigationProps> = ({
    isActive,
    navItems,
    navItemsHeaders,
    onLogoutClick,
    onNavLinkClick,
    className = '',
}): ReactElement => {
    const { pathname } = useLocation()

    const activeHeader = navItemsHeaders.find(header => pathname.startsWith(navItems[header].link)) ?? null

    const [expandedHeaders, setExpandedHeaders] = useState<Record<string, boolean>>(() => (
        activeHeader ? { [activeHeader]: true } : {}
    ))
    const [lastActiveHeader, setLastActiveHeader] = useState(activeHeader)

    if (activeHeader !== lastActiveHeader) {
        setLastActiveHeader(activeHeader)

        if (activeHeader && ! expandedHeaders[activeHeader]) {
            setExpandedHeaders(prev => ({ ...prev, [activeHeader]: true }))
        }
    }

    const toggleHeader = (header: string): void => {
        setExpandedHeaders(prev => ({ ...prev, [header]: ! prev[header] }))
    }

    const wrapperClassName = classnames('collapsible-navigation', {
        'collapsible-navigation--is-active': isActive,
    }, className)

    return (
        <div className={wrapperClassName}>
            <nav className="collapsible-navigation__container">
                <ul className="collapsible-navigation__inner">
                    {navItemsHeaders && navItemsHeaders.map((header) => {
                        const parent = navItems[header]
                        const { children, icon } = parent
                        const isGroup = children.length > 0
                        const isExpanded = !! expandedHeaders[header]

                        return (
                            <li
                                key={header}
                                className="collapsible-navigation__list-item"
                            >
                                {isGroup ? (
                                    <button
                                        type="button"
                                        className={classnames('collapsible-navigation__link-parent', {
                                            'collapsible-navigation__link-parent--active': activeHeader === header,
                                        })}
                                        aria-expanded={isExpanded}
                                        onClick={() => toggleHeader(header)}
                                    >
                                        {icon && <Icon name={icon} className="collapsible-navigation__icon" />}
                                        <span className="collapsible-navigation__label">{parent.label}</span>
                                        <Icon
                                            name="chevron-down"
                                            className={classnames('collapsible-navigation__chevron', {
                                                'collapsible-navigation__chevron--expanded': isExpanded,
                                            })}
                                        />
                                    </button>
                                ) : (
                                    <NavLink
                                        className="collapsible-navigation__link-parent"
                                        onClick={() => onNavLinkClick(header)}
                                        // React Router v7 matches the path literally, a trailing slash would never be active
                                        to={parent.link.replace(/\/$/, '')}
                                    >
                                        {icon && <Icon name={icon} className="collapsible-navigation__icon" />}
                                        <span className="collapsible-navigation__label">{parent.label}</span>
                                    </NavLink>
                                )}

                                {isGroup && (
                                    <div
                                        className={classnames('collapsible-navigation__group-content', {
                                            'collapsible-navigation__group-content--expanded': isExpanded,
                                        })}
                                    >
                                        <ul className="collapsible-navigation__list-children">
                                            {children.map(child => (
                                                <li key={child.link}>
                                                    <NavLink
                                                        to={parent.link + child.link}
                                                        onClick={() => onNavLinkClick(header)}
                                                        className="collapsible-navigation__link collapsible-navigation__link-child"
                                                    >
                                                        {child.label}
                                                    </NavLink>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        )
                    })}

                    <li className="collapsible-navigation__list-item">
                        <Button
                            text={trans('common.logout')}
                            onClick={onLogoutClick}
                            className="collapsible-navigation__link-parent"
                        >
                            <Icon name="log-out" className="collapsible-navigation__icon" />
                            <span className="collapsible-navigation__label">{trans('common.logout')}</span>
                        </Button>
                    </li>

                    <li className="collapsible-navigation__list-item">
                        <div className="collapsible-navigation__version-number">
                            {version}
                        </div>
                    </li>
                </ul>
            </nav>
        </div>
    )
}
